/**
 * LITTER OFFER SCHEMA
 * Schema dla kreatora miotu - OFFER entity type
 * 
 * ============================================
 * WAGI SCORE (suma = 100) - zgodne z MASTER:
 * ============================================
 * - MUST completeness: 40 pkt
 *   (kennel, name, birth_date, status, parents IDs, price, price_includes)
 * 
 * - Images (hero + gallery): 15 pkt
 *   hero: 7 pkt (wymagane 1600px)
 *   gallery: 8 pkt (wymagane min 2 zdjęcia)
 * 
 * - Trust (health tests, parents): 15 pkt
 *   health_tests: 10 pkt
 *   kennel_link: 5 pkt
 * 
 * - CTA/Flow (contact): 15 pkt
 *   contact_method: 10 pkt (dziedziczone z hodowli)
 *   pickup_date: 5 pkt
 * 
 * - Offer/Price: 15 pkt
 *   UWAGA: Liczy się TYLKO gdy:
 *   - price > 0 ORAZ
 *   - selected_price_info.length >= 4
 *   W przeciwnym razie = 0 pkt
 * ============================================
 */

import { FIELD_PRIORITY, CORE_SECTION } from '../core/CoreTypes';

// ==================== SCORE WEIGHTS (MASTER 1:1) ====================

const SCORE_WEIGHTS = {
  // MUST completeness = 40 total
  MUST_NAME: 5,
  MUST_BREED: 5,
  MUST_BIRTH_DATE: 5,
  MUST_STATUS: 3,
  MUST_PUPPIES: 4,
  MUST_FATHER: 5,
  MUST_MOTHER: 5,
  MUST_PRICE: 8,       // Price is critical for OFFER
  
  // Images = 15 total
  IMAGES_HERO: 7,
  IMAGES_GALLERY: 8,   // Gallery more important for litter
  
  // Trust = 15 total
  TRUST_HEALTH: 10,
  TRUST_KENNEL: 5,
  
  // CTA/Flow = 15 total
  CTA_CONTACT: 10,
  CTA_PICKUP: 5,
  
  // Offer/Price = 15 total (on top of MUST price)
  OFFER_PRICE_INFO: 15  // What's included is MUST
};

// ==================== PRICE INCLUDES OPTIONS ====================

export const PRICE_INFO_OPTIONS = [
  { id: 'health_book', label: 'Książeczka zdrowia', required: true },
  { id: 'vaccinations', label: 'Szczepienia', required: true },
  { id: 'deworming', label: 'Odrobaczenie', required: true },
  { id: 'chip', label: 'Chip' },
  { id: 'pedigree', label: 'Rodowód FCI/ZKwP' },
  { id: 'starter_pack', label: 'Starter pack (karma, zabawki)' },
  { id: 'contract', label: 'Umowa kupna-sprzedaży', required: true },
  { id: 'breeder_support', label: 'Wsparcie hodowcy' },
  { id: 'microchip', label: 'Mikrochip' },
  { id: 'insurance', label: 'Ubezpieczenie' },
];

// ==================== HEALTH TEST OPTIONS ====================

export const HEALTH_TEST_OPTIONS = [
  { id: 'hd_a', label: 'HD A/A' },
  { id: 'hd_b', label: 'HD B/B' },
  { id: 'ed_0', label: 'ED 0/0' },
  { id: 'dm_nn', label: 'DM N/N' },
  { id: 'eyes_clear', label: 'Oczy - clear' },
  { id: 'heart_clear', label: 'Serce - clear' },
  { id: 'genetic_clear', label: 'Badania genetyczne - clear' },
  { id: 'patella', label: 'Patella' },
  { id: 'pra_clear', label: 'PRA - clear' },
];

// ==================== LITTER STATUS OPTIONS ====================

export const LITTER_STATUS_OPTIONS = [
  { id: 'planned', label: 'Planowany', color: 'blue' },
  { id: 'available', label: 'Dostępny', color: 'green' },
  { id: 'reservations', label: 'Rezerwacje', color: 'amber' },
  { id: 'sold', label: 'Sprzedany', color: 'gray' },
];

// ==================== FIELD DEFINITIONS ====================

export const litterOfferFields = {
  // ========== STEP 1: Basic Info (MUST) ==========
  kennel_id: {
    name: 'kennel_id',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Hodowla',
    type: 'select',
    step: 1,
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz hodowlę'
    },
    scoreWeight: SCORE_WEIGHTS.TRUST_KENNEL,
    helpText: 'Wybierz hodowlę, do której przypisany jest miot'
  },

  name: {
    name: 'name',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Nazwa/litera miotu',
    type: 'text',
    step: 1,
    validation: {
      required: true,
      minLength: 1,
      maxLength: 50
    },
    errors: {
      required: 'Podaj nazwę lub literę miotu',
      minLength: 'Podaj nazwę miotu'
    },
    scoreWeight: SCORE_WEIGHTS.MUST_NAME,
    placeholder: 'np. Miot A, Miot "Złote Sny"',
    helpText: 'Litera lub nazwa miotu wg rodowodu'
  },

  breed: {
    name: 'breed',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Rasa',
    type: 'select',
    step: 1,
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz rasę'
    },
    scoreWeight: SCORE_WEIGHTS.MUST_BREED,
    helpText: 'Rasa dziedziczona z hodowli (można zmienić)',
    autoFromKennel: true  // Flag: auto-populate from kennel
  },

  species: {
    name: 'species',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Gatunek',
    type: 'select',
    step: 1,
    validation: {
      required: true
    },
    scoreWeight: 0, // Auto-set from kennel
    autoFromKennel: true
  },

  birth_date: {
    name: 'birth_date',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'identity',
    label: 'Data urodzenia',
    type: 'date',
    step: 1,
    validation: {
      required: true
    },
    errors: {
      required: 'Podaj datę urodzenia'
    },
    scoreWeight: SCORE_WEIGHTS.MUST_BIRTH_DATE,
    helpText: 'Data urodzenia szczeniąt'
  },

  status: {
    name: 'status',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'offer',
    label: 'Status miotu',
    type: 'select',
    step: 1,
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz status miotu'
    },
    scoreWeight: SCORE_WEIGHTS.MUST_STATUS,
    options: LITTER_STATUS_OPTIONS
  },

  // ========== STEP 2: Parents as RELATIONS (MUST) ==========
  // UWAGA: Rodzice MUSZĄ być jako ID relacji, nie tylko nazwy
  // To umożliwia: linking, dziedziczenie zdrowia, nawigację AI/SEO
  
  mother_id: {
    name: 'mother_id',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'trust',
    label: 'Matka (suka)',
    type: 'select',  // Select from dogs in kennel
    step: 2,
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz matkę miotu'
    },
    scoreWeight: SCORE_WEIGHTS.MUST_MOTHER,
    helpText: 'Wybierz sukę z hodowli lub dodaj nową',
    isRelation: true,
    relationType: 'dog',
    relationFilter: { gender: 'female' }
  },

  mother_name: {
    name: 'mother_name',
    priority: FIELD_PRIORITY.SHOULD,  // Fallback when mother_id not set
    coreSection: 'trust',
    label: 'Imię matki (ręcznie)',
    type: 'text',
    step: 2,
    validation: {
      minLength: 2
    },
    scoreWeight: 0,
    placeholder: 'np. Luna',
    helpText: 'Używane gdy matka nie jest w systemie',
    isFallback: true,
    fallbackFor: 'mother_id'
  },

  mother_titles: {
    name: 'mother_titles',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Tytuły matki',
    type: 'text',
    step: 2,
    validation: {},
    scoreWeight: 2,
    placeholder: 'np. Ch., JCh., HD A',
    helpText: 'Tytuły i wyniki badań'
  },

  // Father as RELATION (reproduktor)
  father_stud_id: {
    name: 'father_stud_id',
    priority: FIELD_PRIORITY.MUST,
    coreSection: 'trust',
    label: 'Ojciec (reproduktor)',
    type: 'select',
    step: 2,
    validation: {
      required: true
    },
    errors: {
      required: 'Wybierz ojca miotu'
    },
    scoreWeight: SCORE_WEIGHTS.MUST_FATHER,
    helpText: 'Wybierz reproduktora lub dodaj nowego',
    isRelation: true,
    relationType: 'stud',
    relationFilter: { gender: 'male' }
  },

  father_name: {
    name: 'father_name',
    priority: FIELD_PRIORITY.SHOULD,  // Fallback
    coreSection: 'trust',
    label: 'Imię ojca (ręcznie)',
    type: 'text',
    step: 2,
    validation: {
      minLength: 2
    },
    scoreWeight: 0,
    placeholder: 'np. Max',
    helpText: 'Używane gdy ojciec nie jest w systemie',
    isFallback: true,
    fallbackFor: 'father_stud_id'
  },

  father_titles: {
    name: 'father_titles',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Tytuły ojca',
    type: 'text',
    step: 2,
    validation: {},
    scoreWeight: 2,
    placeholder: 'np. Ch., HD A/A, ED 0/0'
  },

  selected_health_tests: {
    name: 'selected_health_tests',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'trust',
    label: 'Badania zdrowotne rodziców',
    type: 'multi-checkbox',
    step: 2,
    validation: {},
    scoreWeight: SCORE_WEIGHTS.TRUST_HEALTH,
    options: HEALTH_TEST_OPTIONS,
    helpText: 'Badania zdrowotne rodziców miotu'
  },

  // ========== STEP 3: Puppies Count ==========
  puppy_count_male: {
    name: 'puppy_count_male',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'offer',
    label: 'Liczba samców',
    type: 'number',
    step: 3,
    validation: {
      min: 0,
      max: 20
    },
    scoreWeight: 2,
    placeholder: '0'
  },

  puppy_count_female: {
    name: 'puppy_count_female',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'offer',
    label: 'Liczba samic',
    type: 'number',
    step: 3,
    validation: {
      min: 0,
      max: 20
    },
    scoreWeight: 2,
    placeholder: '0'
  },

  available_count: {
    name: 'available_count',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'offer',
    label: 'Dostępnych',
    type: 'number',
    step: 3,
    validation: {
      min: 0
    },
    scoreWeight: SCORE_WEIGHTS.MUST_PUPPIES,
    helpText: 'Ile szczeniąt jest jeszcze dostępnych'
  },

  reserved_count: {
    name: 'reserved_count',
    priority: FIELD_PRIORITY.OPTIONAL,
    coreSection: 'offer',
    label: 'Zarezerwowanych',
    type: 'number',
    step: 3,
    validation: {
      min: 0
    },
    scoreWeight: 0
  },

  // ========== STEP 4: Price (SHOULD for initial save) ==========
  price: {
    name: 'price',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'offer',
    label: 'Cena szczenięcia',
    type: 'number',
    step: 4,
    validation: {
      min: 0
    },
    errors: {
      min: 'Cena nie może być ujemna'
    },
    scoreWeight: SCORE_WEIGHTS.MUST_PRICE,
    placeholder: 'np. 5000',
    helpText: 'Cena w PLN (możesz dodać później)'
  },

  selected_price_info: {
    name: 'selected_price_info',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'offer',
    label: 'Co zawiera cena',
    type: 'multi-checkbox',
    step: 4,
    validation: {},
    scoreWeight: SCORE_WEIGHTS.OFFER_PRICE_INFO,
    options: PRICE_INFO_OPTIONS,
    helpText: 'Klienci chcą wiedzieć, co dostaną w cenie'
  },

  pickup_date: {
    name: 'pickup_date',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'offer',
    label: 'Możliwy odbiór od',
    type: 'date',
    step: 4,
    validation: {},
    scoreWeight: SCORE_WEIGHTS.CTA_PICKUP,
    helpText: 'Od kiedy szczenięta będą gotowe do odbioru'
  },

  // ========== STEP 5: Description ==========
  description: {
    name: 'description',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'hero',
    label: 'Opis miotu',
    type: 'textarea',
    step: 5,
    validation: {
      minLength: 50
    },
    scoreWeight: 10,
    placeholder: 'Opisz miot - charakter szczeniąt, warunki, dlaczego warto...',
    helpText: 'Dobry opis zwiększa konwersję o 40%'
  },

  // ========== STEP 6: Images (SHOULD — user can add later) ==========
  cover_image: {
    name: 'cover_image',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'hero',
    label: 'Zdjęcie główne',
    type: 'image',
    step: 6,
    validation: {},
    scoreWeight: SCORE_WEIGHTS.IMAGES_HERO,
    helpText: 'Min. 1600x900px - możesz dodać później'
  },

  gallery: {
    name: 'gallery',
    priority: FIELD_PRIORITY.SHOULD,
    coreSection: 'gallery',
    label: 'Galeria zdjęć',
    type: 'image-array',
    step: 6,
    validation: {
      maxItems: 20
    },
    scoreWeight: SCORE_WEIGHTS.IMAGES_GALLERY,
    helpText: 'Mioty ze zdjęciami sprzedają się 5x szybciej — możesz dodać później'
  }
};

// ==================== STEP CONFIGURATION ====================
// 
// UWAGA: Step 2 (Rodzice) akceptuje ALBO ID relacji ALBO tekst ręczny
// - mother_id / father_stud_id = powiązanie z bazą (preferowane)
// - mother_name / father_name = fallback gdy rodzic spoza systemu
//

export const litterSteps = [
  {
    id: 1,
    name: 'Podstawowe info',
    subtitle: 'Hodowla, nazwa i data urodzenia',
    fields: ['kennel_id', 'name', 'breed', 'species', 'birth_date', 'status'],
    mustFields: ['kennel_id', 'name', 'breed', 'birth_date', 'status'],
    mustGate: true
  },
  {
    id: 2,
    name: 'Rodzice',
    subtitle: 'Dane rodziców miotu',
    // Include both ID and name fields - validation is flexible (ID OR name)
    fields: ['mother_id', 'mother_name', 'mother_titles', 'father_stud_id', 'father_name', 'father_titles', 'selected_health_tests'],
    // Custom validation: requires (mother_id OR mother_name) AND (father_stud_id OR father_name)
    mustFields: [],  // Empty - we use custom hasParentsInfo() check
    customValidation: 'parents',  // Flag for custom parent validation
    mustGate: true
  },
  {
    id: 3,
    name: 'Szczenięta',
    subtitle: 'Ile szczeniąt w miocie?',
    fields: ['puppy_count_male', 'puppy_count_female', 'available_count', 'reserved_count'],
    mustFields: [],
    mustGate: false
  },
  {
    id: 4,
    name: 'Cena i oferta',
    subtitle: 'Cena i co zawiera (opcjonalne — możesz dodać później)',
    fields: ['price', 'selected_price_info', 'pickup_date'],
    mustFields: [],
    mustGate: false
  },
  {
    id: 5,
    name: 'O miocie',
    subtitle: 'Opowiedz o miocie',
    fields: ['description'],
    mustFields: [],
    mustGate: false
  },
  {
    id: 6,
    name: 'Zdjęcia',
    subtitle: 'Zdjęcia miotu (opcjonalne — możesz dodać później)',
    fields: ['cover_image', 'gallery'],
    mustFields: [],
    mustGate: false
  }
];

// ==================== SCHEMA EXPORT ====================

export const litterOfferSchema = {
  entityType: 'OFFER',
  profileId: 'litter_default',
  steps: litterSteps,
  fields: litterOfferFields
};

// ==================== VALIDATION HELPERS ====================

/**
 * Check if price info has minimum required items
 * Zmieniono z 4 na 2 - prostszy UX
 */
export function hasSufficientPriceInfo(formData) {
  const selected = formData.selected_price_info || [];
  return selected.length >= 2;
}

/**
 * Check if gallery has minimum required images
 * Zmieniono z 6 na 2 - dla małych miotów 6 zdjęć to przesada
 */
export function hasSufficientGallery(formData) {
  const gallery = formData.gallery || [];
  return gallery.length >= 2;
}

/**
 * Check if both parents are filled
 * 
 * WAŻNE: Akceptuje ALBO ID relacji ALBO tekst ręczny:
 * - mother_id (preferowane) LUB mother_name (fallback)
 * - father_stud_id (preferowane) LUB father_name (fallback)
 * 
 * To pozwala na:
 * 1. Powiązanie z istniejącymi psami w bazie (linking, SEO, dziedziczenie danych)
 * 2. Ręczne wpisanie gdy rodzic spoza systemu (np. obcy reproduktor)
 */
export function hasParentsInfo(formData) {
  // Mother: accept ID or name
  const hasMother = (
    (formData.mother_id && formData.mother_id.trim?.().length > 0) ||
    (formData.mother_name && formData.mother_name.trim().length >= 2)
  );
  
  // Father: accept stud_id or name
  const hasFather = (
    (formData.father_stud_id && formData.father_stud_id.trim?.().length > 0) ||
    (formData.father_name && formData.father_name.trim().length >= 2)
  );
  
  return hasMother && hasFather;
}

/**
 * Check if mother info is provided (ID or name)
 */
export function hasMotherInfo(formData) {
  return (
    (formData.mother_id && formData.mother_id.trim?.().length > 0) ||
    (formData.mother_name && formData.mother_name.trim().length >= 2)
  );
}

/**
 * Check if father info is provided (ID or name)
 */
export function hasFatherInfo(formData) {
  return (
    (formData.father_stud_id && formData.father_stud_id.trim?.().length > 0) ||
    (formData.father_name && formData.father_name.trim().length >= 2)
  );
}

/**
 * Get missing MUST items with user-friendly messages
 */
export function getLitterMissingItems(formData) {
  const missing = [];
  
  if (!formData.price || formData.price <= 0) {
    missing.push({ 
      field: 'price', 
      label: 'Cena szczenięcia',
      step: 4,
      message: 'Dodaj cenę szczenięcia'
    });
  }
  
  if (!hasSufficientPriceInfo(formData)) {
    const count = (formData.selected_price_info || []).length;
    missing.push({ 
      field: 'selected_price_info', 
      label: 'Co zawiera cena',
      step: 4,
      message: `Dodaj co zawiera cena (masz ${count}/2 min.)`
    });
  }
  
  if (!formData.cover_image) {
    missing.push({ 
      field: 'cover_image', 
      label: 'Zdjęcie główne',
      step: 6,
      message: 'Dodaj zdjęcie główne miotu'
    });
  }
  
  if (!hasSufficientGallery(formData)) {
    const count = (formData.gallery || []).length;
    missing.push({ 
      field: 'gallery', 
      label: 'Galeria zdjęć',
      step: 6,
      message: `Dodaj zdjęcia miotu (masz ${count}/2 min.)`
    });
  }
  
  // Parent info - check for ID OR name
  if (!hasMotherInfo(formData)) {
    missing.push({ 
      field: 'mother_id', 
      label: 'Matka miotu',
      step: 2,
      message: 'Wybierz lub wpisz matkę miotu'
    });
  }
  if (!hasFatherInfo(formData)) {
    missing.push({ 
      field: 'father_stud_id', 
      label: 'Ojciec miotu',
      step: 2,
      message: 'Wybierz lub wpisz ojca miotu'
    });
  }
  
  return missing;
}

/**
 * Image requirements for OFFER
 */
export const IMAGE_REQUIREMENTS = {
  cover_image: {
    required: true,
    minWidth: 1600,
    minHeight: 900,
    message: "Zdjęcie główne musi mieć min. 1600x900px"
  },
  gallery: {
    minCount: 2,
    recommendedCount: 6,
    minWidth: 1200,
    message: "Dodaj min. 2 zdjęcia w rozdzielczości 1200px+"
  }
};

export default litterOfferSchema;
